import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import TabletMacOutlinedIcon from "@mui/icons-material/TabletMacOutlined";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";

/* -------------------------------------------------------------------------- */
/*  Design tokens – same values as ActiveSessionsList so both match           */
/* -------------------------------------------------------------------------- */

const MUTED = "#92a9a9";
const DIVIDER = "rgba(146, 169, 169, 0.2)";
const MONO_FONT = '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace';
const RADIUS = 5;
const DIALOG_MAX_WIDTH = 460; // px. Explicit on purpose, see the note on breakpoints below.

const FS = { title: 16, body: 14, small: 13, caption: 12, label: 11 };

// Countdown ring around the header icon
const RING_SIZE = 44;
const RING_STROKE = 2;
const RING_R = (RING_SIZE - RING_STROKE) / 2;
const RING_C = 2 * Math.PI * RING_R;

const EMPTY_VALUES = ["", "—", "-", "unknown"];

const isEmpty = (value) =>
    value === null ||
    value === undefined ||
    EMPTY_VALUES.includes(String(value).trim().toLowerCase());

const DEVICE_ICONS = {
    desktop: DesktopWindowsOutlinedIcon,
    mobile: SmartphoneOutlinedIcon,
    tablet: TabletMacOutlinedIcon,
};

/**
 * "desktop Chrome Linux" -> { kind: "desktop", name: "Chrome on Linux" }
 * Anything that doesn't match that shape is shown as-is.
 */
function parseDevice(raw) {
    if (isEmpty(raw)) return { kind: null, name: "Unknown device" };

    const parts = String(raw).trim().split(/\s+/);
    const kind = parts[0].toLowerCase();

    if (parts.length >= 3 && DEVICE_ICONS[kind]) {
        return { kind, name: `${parts[1]} on ${parts.slice(2).join(" ")}` };
    }
    return { kind: null, name: String(raw).trim() };
}

function normalizeSession(raw) {
    return {
        id: raw.session_id,
        device: raw.device ?? raw.login_device ?? raw.active_device,
        ip: raw.ip_addr ?? raw.ip_address ?? raw.location_ip ?? raw.ip,
        location: raw.location ?? raw.active_location,
        loginTime: raw.login_time,
        lastVisit: raw.last_visit,
    };
}

/* -------------------------------------------------------------------------- */
/*  Session row – reads like a ledger line: who, where, then the data cells    */
/* -------------------------------------------------------------------------- */

function DataCell({ label, value, mono = false }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography
                sx={{ fontSize: FS.label, lineHeight: 1.4, color: MUTED }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: mono ? FS.caption : FS.small,
                    lineHeight: 1.5,
                    fontFamily: mono ? MONO_FONT : "inherit",
                    fontVariantNumeric: "tabular-nums",
                    color: "text.primary",
                    wordBreak: "break-word",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

function SignedOutTag() {
    const theme = useTheme();

    return (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                flexShrink: 0,
                px: 1,
                height: 22,
                borderRadius: "11px",
                border: `1px solid ${DIVIDER}`,
                fontSize: FS.label,
                fontWeight: 600,
                color: MUTED,
                whiteSpace: "nowrap",
            }}
        >
            <Box
                component="span"
                aria-hidden
                sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: theme.palette.error.main,
                }}
            />
            Signed out
        </Box>
    );
}

function SessionRow({ session, isLast }) {
    const theme = useTheme();
    const { kind, name } = parseDevice(session.device);
    const KindIcon = DEVICE_ICONS[kind] ?? DevicesOtherIcon;

    // Same labels as the active sessions list. Empty fields are skipped, so
    // the payload { device, location, ip_addr } shows Login location + IP address.
    const cells = [
        { label: "IP address", value: session.ip, mono: true },
        { label: "Login location", value: session.location },
        { label: "Login time", value: session.loginTime, mono: true },
        { label: "Last visit", value: session.lastVisit, mono: true },
    ].filter((cell) => !isEmpty(cell.value));

    return (
        <Box
            sx={{
                px: 3,
                py: 2,
                borderBottom: isLast ? "none" : `1px solid ${DIVIDER}`,
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                    aria-hidden
                    sx={{
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "8px",
                        bgcolor: alpha(theme.palette.common.white, 0.05),
                        color: MUTED,
                    }}
                >
                    <KindIcon sx={{ fontSize: 18 }} />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Typography
                            sx={{
                                fontSize: FS.body,
                                fontWeight: 600,
                                lineHeight: "20px",
                                minWidth: 0,
                                wordBreak: "break-word",
                            }}
                        >
                            {name}
                        </Typography>
                        <SignedOutTag />
                    </Stack>
                </Box>
            </Stack>

            {cells.length > 0 && (
                <Box
                    sx={{
                        mt: 1.5,
                        ml: "44px", // icon (32) + gap (12): aligns cells under the device name
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(160px, 1fr))",
                        gap: "10px 16px",
                    }}
                >
                    {cells.map((cell) => (
                        <DataCell
                            key={cell.label}
                            label={cell.label}
                            value={cell.value}
                            mono={cell.mono}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

/* -------------------------------------------------------------------------- */
/*  Header icon with the countdown ring                                        */
/* -------------------------------------------------------------------------- */

function CountdownIcon({ autoCloseMs, paused }) {
    const theme = useTheme();
    const warning = theme.palette.warning.main;

    return (
        <Box
            aria-hidden
            sx={{
                position: "relative",
                width: RING_SIZE,
                height: RING_SIZE,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                "& svg.ring": {
                    position: "absolute",
                    inset: 0,
                    transform: "rotate(-90deg)",
                },
                "& .ring-track": {
                    fill: "none",
                    stroke: alpha(warning, 0.2),
                    strokeWidth: RING_STROKE,
                },
                "@keyframes invalidatedSessionsRing": {
                    from: { strokeDashoffset: 0 },
                    to: { strokeDashoffset: RING_C },
                },
                "& .ring-progress": {
                    fill: "none",
                    stroke: warning,
                    strokeWidth: RING_STROKE,
                    strokeLinecap: "round",
                    strokeDasharray: RING_C,
                    animation: `invalidatedSessionsRing ${autoCloseMs}ms linear forwards`,
                    animationPlayState: paused ? "paused" : "running",
                },
            }}
        >
            <svg
                className="ring"
                width={RING_SIZE}
                height={RING_SIZE}
                viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
            >
                <circle
                    className="ring-track"
                    cx={RING_SIZE / 2}
                    cy={RING_SIZE / 2}
                    r={RING_R}
                />
                <circle
                    className="ring-progress"
                    cx={RING_SIZE / 2}
                    cy={RING_SIZE / 2}
                    r={RING_R}
                />
            </svg>
            <SecurityOutlinedIcon sx={{ fontSize: 20, color: warning }} />
        </Box>
    );
}

/* -------------------------------------------------------------------------- */
/*  Main export                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Popup shown right after login when other sessions were signed out.
 *
 * Props
 *  - open:         boolean
 *  - sessions:     the `invalidated_sessions` array from the login response
 *  - onClose:      called on auto-close, on "Got it", on backdrop click and on Esc
 *  - autoCloseMs:  delay before closing automatically (default 5000, shown as 5, 4, 3...)
 *
 * The ring around the header icon empties over `autoCloseMs`. Hovering the
 * popup pauses both the ring and the timer.
 *
 * Sizing note: the width is set in plain px on purpose. This app's theme uses
 * custom breakpoint names (mobile / tablet / laptop), so MUI's maxWidth="sm"
 * and { xs, sm } responsive values don't resolve here and the popup stretched
 * across the whole screen.
 */
export default function InvalidatedSessionsDialog({
    open,
    sessions,
    onClose,
    autoCloseMs = 10000,
}) {
    const [paused, setPaused] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(
        Math.ceil(autoCloseMs / 1000),
    );

    const remainingRef = useRef(autoCloseMs);

    // Countdown that can be paused and resumed without losing elapsed time.
    // Ticks every 100ms so the number changes exactly on the second and the
    // dialog closes when it reaches zero.
    useEffect(() => {
        if (!open) {
            remainingRef.current = autoCloseMs;
            setSecondsLeft(Math.ceil(autoCloseMs / 1000));
            setPaused(false); // a dialog closed while hovered must not reopen paused
            return undefined;
        }
        if (paused) return undefined;

        const startedAt = Date.now();
        const startRemaining = remainingRef.current;

        const ticker = setInterval(() => {
            const left = startRemaining - (Date.now() - startedAt);
            setSecondsLeft(Math.max(0, Math.ceil(left / 1000)));
            if (left <= 0) {
                clearInterval(ticker);
                onClose();
            }
        }, 100);

        return () => {
            clearInterval(ticker);
            remainingRef.current = Math.max(
                0,
                startRemaining - (Date.now() - startedAt),
            );
        };
    }, [open, paused, autoCloseMs, onClose]);

    const items = (sessions ?? []).map(normalizeSession);
    if (!items.length) return null;

    const count = items.length;
    const title =
        count === 1
            ? "Signed out of 1 other device"
            : `Signed out of ${count} other devices`;
    const subtitle = `${
        count === 1 ? "That session ended" : "Those sessions ended"
    } when you logged in. If this wasn't you, change your password.`;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            aria-labelledby="invalidated-sessions-title"
            aria-describedby="invalidated-sessions-description"
            PaperProps={{
                onMouseEnter: () => setPaused(true),
                onMouseLeave: () => setPaused(false),
                sx: {
                    m: 2,
                    width: "calc(100% - 32px)",
                    maxWidth: DIALOG_MAX_WIDTH,
                    borderRadius: RADIUS,
                    border: `1px solid ${DIVIDER}`,
                    backgroundImage: "none",
                    overflow: "hidden",
                },
            }}
        >
            {/* Header */}
            <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{
                    px: 3,
                    pt: 3,
                    pb: 2.5,
                    borderBottom: `1px solid ${DIVIDER}`,
                }}
            >
                <CountdownIcon autoCloseMs={autoCloseMs} paused={paused} />

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        id="invalidated-sessions-title"
                        component="h2"
                        sx={{
                            fontSize: FS.title,
                            fontWeight: 600,
                            lineHeight: "24px",
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        id="invalidated-sessions-description"
                        sx={{
                            fontSize: FS.small,
                            lineHeight: 1.5,
                            color: "text.secondary",
                            mt: 0.25,
                        }}
                    >
                        {subtitle}
                    </Typography>
                </Box>

                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    size="small"
                    sx={{
                        alignSelf: "flex-start",
                        mt: -1,
                        mr: -1,
                        color: MUTED,
                        "&:hover": { color: "text.primary" },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Stack>

            {/* Sessions */}
            <Box sx={{ maxHeight: "50vh", overflowY: "auto" }}>
                {items.map((session, index) => (
                    <SessionRow
                        key={session.id ?? index}
                        session={session}
                        isLast={index === items.length - 1}
                    />
                ))}
            </Box>

            {/* Footer */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{
                    px: 3,
                    py: 1.5,
                    borderTop: `1px solid ${DIVIDER}`,
                    bgcolor: "rgba(0, 0, 0, 0.18)",
                }}
            >
                <Typography
                    sx={{
                        fontSize: FS.caption,
                        color: MUTED,
                        lineHeight: 1.5,
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    {paused
                        ? "Paused while you read"
                        : `Closing in ${secondsLeft}s`}
                </Typography>
                <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={onClose}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: FS.small,
                        px: 2,
                        borderColor: DIVIDER,
                        color: "#ffffff",
                    }}
                >
                    Ok
                </Button>
            </Stack>
        </Dialog>
    );
}
